import InfoIcon from "@mui/icons-material/Info";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import { useCheckUserPermission } from "../../../../api/hooks/useCheckUserPermission";
import useColors from "../../../../api/hooks/useColors";
import { controlKeys } from "../../../../api/queryKeys";
import CustomToolTip from "../../../../components/CustomTooltip";
import CustomToolTipWithPermission from "../../../../components/CustomToolTipWithPermission";
import { Toast } from "../../../../components/Toast";
import { RemediationTypeChip } from "../../../../utils/chipsData";
import {
  PLATFORM_USER_ACTIONS,
  VIEWER_ACCESS_DENIED_MESSAGE,
} from "../../../../utils/constants";
import { getPrettyDateTime, useLocalTime } from "../../../../utils/timestamp";
import ChipColor from "../../../remediation/plan/components/ChipColor";

const CustomSeveritySelect = ({ row, renderValue }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { updateControlCustomSeverity, trackEvent } = useAPI();
  const showLocalTime = useLocalTime();
  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.UPDATE_CONTROL_CUSTOM_SEVERITY
  );
  const options = Object.entries(RemediationTypeChip["controlPriority"])
    .filter(([key, value]) => key !== "0")
    .map(([key, value]) => ({
      value: key,
      label: value.label,
    }))
    .reverse();
  const queryClient = useQueryClient();
  const open = Boolean(anchorEl);
  const colors = useColors();
  const handleClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (optionValue) => {
    trackEvent(
      `user updated control custom severity ${row.control_id} to ${optionValue}`
    );
    updateControlCustomSeverity({
      control_id: row.control_id,
      custom_severity: optionValue,
    }).then(() => {
      queryClient.invalidateQueries({
        queryKey: controlKeys.all,
      });
      handleClose();
      Toast.success("Severity updated successfully");
    });
  };

  const currentSeverity = row.severity;
  const isCustomSeverity = row.severity !== row.original_severity;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <CustomToolTipWithPermission
        title={hasPermission ? "" : VIEWER_ACCESS_DENIED_MESSAGE}
        permission={hasPermission}
        placement="top"
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CustomToolTip
            title={!hasPermission ? "" : "Adjust severity level"}
            placement="top"
          >
            <Box
              onClick={hasPermission ? handleClick : null}
              sx={{ display: "inline-block", cursor: "pointer" }}
            >
              {renderValue ? renderValue(currentSeverity) : currentSeverity}
            </Box>
          </CustomToolTip>
        </Box>
      </CustomToolTipWithPermission>

      {isCustomSeverity && (
        <CustomToolTip
          title={`This severity was manually updated ${
            row.severity_details
              ? `by ${row.severity_details?.changed_by} on ${getPrettyDateTime(
                  row.severity_details?.changed_at?.timestamp_utc,
                  showLocalTime
                )}`
              : ""
          }. Original severity was '${
            RemediationTypeChip["controlPriority"][row.original_severity]
              ?.label || row.original_severity
          }'`}
          placement="right"
          sx={{ maxWidth: "260px" }}
        >
          <IconButton
            size="small"
            sx={{ padding: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <InfoIcon
              fontSize="small"
              sx={{
                width: "20px",
                height: "20px",
                color: colors.grey_48percent,
              }}
            />
          </IconButton>
        </CustomToolTip>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "severity-select-button",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          color: colors.contrast_text_dark,
          "& .MuiPaper-root": {
            borderRadius: "8px",
            minWidth: "100px",
            border: "1px solid " + colors.divider2,
            boxShadow: "0px 1px 4px 0px " + colors.black_10percent,
          },
          "& .MuiList-root": {
            padding: "4px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          },
        }}
      >
        {options
          .filter((option) => option.value !== currentSeverity)
          .map((option) => (
            <MenuItem
              key={option.value}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleMenuItemClick(option.value);
              }}
            >
              <ChipColor
                value={option.value}
                type="controlPriority"
                sx={{ cursor: "pointer" }}
                width={100}
              />
            </MenuItem>
          ))}
      </Menu>
    </Box>
  );
};

export default CustomSeveritySelect;
